/*
 * Copyright (c) 2021 Omnicell. All rights reserved.
 *
 * This program contains proprietary, trade secret information of
 * Omnicell. and may not be copied, reproduced or reverse engineered
 * in any way without the prior written permission of Omnicell.
 */

import { datadogRum } from '@datadog/browser-rum';
import axios from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { BrandContext } from '../contexts/BrandContext';
import DataProvider from '../providers/DataProvider';
import ChatBot from './ChatBot';

// Wraps entire bot. Responsible for getting EH branding data and setting session based state.
// Will not render chatbot until REST calls are finished.
const ChatBotWrapper = ({ sessionId }) => {
  const { botId } = useParams();
  const [searchParams] = useSearchParams();
  const { setBrand, setLogo } = useContext(BrandContext);
  const [brandNotFound, setBrandNotFound] = useState(false);
  const [canRender, setCanRender] = useState(false);

  useEffect(() => {
    getBotDetails();
    setDatadogContext();
  }, []);

  const getBotDetails = async () => {
    const isProd = process.env.REACT_APP_ENV === 'prod';
    const baseUrl = isProd
      ? process.env.REACT_APP_CS_API_PROD
      : process.env.REACT_APP_CS_API_STAGING;

    try {
      const response = await axios.get(`${baseUrl}/api/configs`, {
        headers: {
          sessionId,
          chatbotId: botId
        }
      });

      const brand = buildBrandConfig(response.data.data, baseUrl);
      setBrand(brand);
      const logoUrl = response.data.data.logoUrl
        ? `${response.data.data.logoUrl}?t=${Date.now()}`
        : './assets/logo.png';
      setLogo(logoUrl);
    } catch {
      setLogo('./assets/logo.png');
      setBrandNotFound(true);
    } finally {
      setCanRender(true);
    }
  };

  const buildBrandConfig = (config, baseUrl) => ({
    id: config.id,
    botType: 'CS',
    fontColor: config.fontColor || '#ffffff',
    primaryColor: config.primaryColor || '#2c818f',
    title: config.name || 'No chatbot name',
    messagingEndpoint: `${baseUrl}/api/messages`,
    websocketEndpoint: `${baseUrl}/api/websocket_messages`
  });

  const setDatadogContext = () => {
    datadogRum.setRumGlobalContext({ 'bot.uuid': botId });
  };

  return (
    <DataProvider>
      {canRender && (
        <ChatBot
          botId={botId}
          invalidBotId={brandNotFound}
          sessionId={sessionId}
          searchParams={searchParams}
        />
      )}
    </DataProvider>
  );
};

export default ChatBotWrapper;
